import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Scale, Delete, Check, ArrowLeft, User, AlertCircle, Hash } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AthleteWeightKiosk() {
  const [step, setStep] = useState('identifier'); // 'identifier' or 'weight'
  const [identifierType, setIdentifierType] = useState('jersey'); // 'jersey' or 'locker'
  const [identifierValue, setIdentifierValue] = useState('');
  const [weight, setWeight] = useState('');
  const [playerName, setPlayerName] = useState('');
  const [playerId, setPlayerId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleKeyPress = (key) => {
    if (step === 'identifier') {
      if (identifierValue.length < 4) {
        setIdentifierValue(prev => prev + key);
      }
    } else {
      // Allow decimal point once
      if (key === '.' && weight.includes('.')) return;
      if (weight.length < 6) {
        setWeight(prev => prev + key);
      }
    }
    setError('');
  };

  const handleDelete = () => {
    if (step === 'identifier') {
      setIdentifierValue(prev => prev.slice(0, -1));
    } else {
      setWeight(prev => prev.slice(0, -1));
    }
  };

  const handleClear = () => {
    if (step === 'identifier') {
      setIdentifierValue('');
    } else {
      setWeight('');
    }
  };

  const lookupPlayer = async () => {
    if (!identifierValue) {
      setError(`Please enter your ${identifierType === 'jersey' ? 'jersey number' : 'locker number'}`);
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Use public kiosk endpoint (no auth required)
      const res = await axios.get(`${BACKEND_URL}/api/kiosk/players`);
      let player;
      
      if (identifierType === 'jersey') {
        player = res.data.find(p => p.jersey_number === parseInt(identifierValue));
      } else {
        player = res.data.find(p => p.locker_number === identifierValue);
      }
      
      if (player) {
        setPlayerName(player.name);
        setPlayerId(player.id);
        setStep('weight');
      } else {
        setError(`No player found with ${identifierType === 'jersey' ? 'jersey' : 'locker'} #${identifierValue}`);
      }
    } catch (err) {
      setError('Unable to find player. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const submitWeight = async () => {
    if (!weight || parseFloat(weight) <= 0) {
      setError('Please enter a valid weight');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Use public kiosk endpoint (no auth required)
      await axios.post(`${BACKEND_URL}/api/kiosk/weight`, {
        player_id: playerId,
        weight: parseFloat(weight)
      });
      
      setSuccess(true);
      toast.success('Weight logged successfully!');
      
      // Reset after 3 seconds
      setTimeout(() => {
        setStep('identifier');
        setIdentifierValue('');
        setWeight('');
        setPlayerName('');
        setPlayerId(null);
        setSuccess(false);
      }, 3000);
    } catch (err) {
      setError('Failed to log weight. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const goBack = () => {
    setStep('identifier');
    setWeight('');
    setError('');
  };

  // Success screen
  if (success) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center p-4">
        <div className="text-center animate-fade-in-up">
          <div className="w-32 h-32 bg-[#32D74B] rounded-full flex items-center justify-center mx-auto mb-8">
            <Check className="w-16 h-16 text-white" />
          </div>
          <h1 className="font-display text-5xl lg:text-6xl text-[#32D74B] mb-4">WIN THE DAY!</h1>
          <p className="text-2xl text-[#A1A1AA]">
            {playerName} - {weight} lbs
          </p>
          <p className="text-lg text-[#71717A] mt-4">Returning to start...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex flex-col">
      {/* Header */}
      <header className="bg-[#121212] border-b border-[#27272A] p-4 lg:p-6">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 lg:w-16 lg:h-16 bg-[#FF3B30] flex items-center justify-center">
              <Scale className="w-6 h-6 lg:w-8 lg:h-8 text-white" />
            </div>
            <div>
              <h1 className="font-display text-2xl lg:text-4xl text-white">WEIGHT CHECK-IN</h1>
              <p className="text-[#71717A] text-sm lg:text-base">Athlete Self-Service</p>
            </div>
          </div>
          {step === 'weight' && (
            <Button
              onClick={goBack}
              variant="outline"
              className="border-[#27272A] text-[#A1A1AA] hover:bg-[#1A1A1A]"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back
            </Button>
          )}
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex flex-col items-center justify-center p-4 lg:p-8">
        <div className="w-full max-w-xl">
          {/* Step Indicator */}
          <div className="flex items-center justify-center gap-4 mb-8">
            <div className={`flex items-center gap-2 ${step === 'identifier' ? 'text-[#FF3B30]' : 'text-[#32D74B]'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                step === 'identifier' ? 'bg-[#FF3B30] text-white' : 'bg-[#32D74B] text-black'
              }`}>
                {step === 'weight' ? <Check className="w-5 h-5" /> : '1'}
              </div>
              <span className="hidden sm:inline font-medium">ID #</span>
            </div>
            <div className="w-12 h-0.5 bg-[#27272A]" />
            <div className={`flex items-center gap-2 ${step === 'weight' ? 'text-[#FF3B30]' : 'text-[#71717A]'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                step === 'weight' ? 'bg-[#FF3B30] text-white' : 'bg-[#27272A] text-[#71717A]'
              }`}>
                2
              </div>
              <span className="hidden sm:inline font-medium">Weight</span>
            </div>
          </div>

          {/* Identifier Type Toggle - Only on first step */}
          {step === 'identifier' && (
            <div className="flex gap-2 mb-6">
              <button
                onClick={() => { setIdentifierType('jersey'); setIdentifierValue(''); }}
                className={`flex-1 p-3 border font-medium transition-all ${
                  identifierType === 'jersey'
                    ? 'bg-[#FF3B30] border-[#FF3B30] text-white'
                    : 'bg-[#121212] border-[#27272A] text-[#71717A] hover:border-[#3F3F46]'
                }`}
                data-testid="jersey-type-btn"
              >
                <User className="w-5 h-5 mx-auto mb-1" />
                Jersey #
              </button>
              <button
                onClick={() => { setIdentifierType('locker'); setIdentifierValue(''); }}
                className={`flex-1 p-3 border font-medium transition-all ${
                  identifierType === 'locker'
                    ? 'bg-[#FF3B30] border-[#FF3B30] text-white'
                    : 'bg-[#121212] border-[#27272A] text-[#71717A] hover:border-[#3F3F46]'
                }`}
                data-testid="locker-type-btn"
              >
                <Hash className="w-5 h-5 mx-auto mb-1" />
                Locker #
              </button>
            </div>
          )}

          {/* Display */}
          <div className="bg-[#121212] border border-[#27272A] p-6 lg:p-8 mb-6">
            {step === 'identifier' ? (
              <>
                <p className="text-center text-[#71717A] uppercase tracking-widest text-sm mb-4">
                  Enter Your {identifierType === 'jersey' ? 'Jersey' : 'Locker'} Number
                </p>
                <div className="flex items-center justify-center gap-4">
                  <span className="text-[#FF3B30] font-display text-6xl lg:text-8xl">#</span>
                  <div 
                    className="font-display text-7xl lg:text-9xl text-white min-w-[200px] text-center"
                    data-testid="identifier-display"
                  >
                    {identifierValue || <span className="text-[#27272A]">00</span>}
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className="flex items-center justify-center gap-3 mb-4">
                  <User className="w-6 h-6 text-[#32D74B]" />
                  <span className="text-[#32D74B] text-xl font-medium">#{identifierValue} - {playerName}</span>
                </div>
                <p className="text-center text-[#71717A] uppercase tracking-widest text-sm mb-4">
                  Enter Your Weight (lbs)
                </p>
                <div 
                  className="font-display text-7xl lg:text-9xl text-white text-center"
                  data-testid="weight-display"
                >
                  {weight || <span className="text-[#27272A]">000</span>}
                  <span className="text-3xl lg:text-4xl text-[#71717A] ml-2">lbs</span>
                </div>
              </>
            )}
          </div>

          {/* Error Message */}
          {error && (
            <div className="flex items-center gap-3 p-4 bg-[#FF3B30]/10 border border-[#FF3B30]/30 mb-6">
              <AlertCircle className="w-5 h-5 text-[#FF3B30] flex-shrink-0" />
              <p className="text-[#FF3B30]">{error}</p>
            </div>
          )}

          {/* Keypad */}
          <div className="grid grid-cols-3 gap-3 lg:gap-4 mb-6">
            {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((num) => (
              <button
                key={num}
                onClick={() => handleKeyPress(num)}
                className="aspect-square bg-[#1A1A1A] border border-[#27272A] text-white font-display text-4xl lg:text-5xl 
                  hover:bg-[#27272A] hover:border-[#3F3F46] active:scale-95 transition-all"
                data-testid={`key-${num}`}
              >
                {num}
              </button>
            ))}
            <button
              onClick={handleClear}
              className="aspect-square bg-[#27272A] border border-[#3F3F46] text-[#A1A1AA] font-bold text-lg
                hover:bg-[#3F3F46] active:scale-95 transition-all"
              data-testid="key-clear"
            >
              CLR
            </button>
            <button
              onClick={() => handleKeyPress('0')}
              className="aspect-square bg-[#1A1A1A] border border-[#27272A] text-white font-display text-4xl lg:text-5xl
                hover:bg-[#27272A] hover:border-[#3F3F46] active:scale-95 transition-all"
              data-testid="key-0"
            >
              0
            </button>
            {step === 'weight' ? (
              <button
                onClick={() => handleKeyPress('.')}
                className="aspect-square bg-[#1A1A1A] border border-[#27272A] text-white font-display text-4xl lg:text-5xl
                  hover:bg-[#27272A] hover:border-[#3F3F46] active:scale-95 transition-all"
                data-testid="key-decimal"
              >
                .
              </button>
            ) : (
              <button
                onClick={handleDelete}
                className="aspect-square bg-[#27272A] border border-[#3F3F46] text-[#A1A1AA] flex items-center justify-center
                  hover:bg-[#3F3F46] active:scale-95 transition-all"
                data-testid="key-delete"
              >
                <Delete className="w-8 h-8" />
              </button>
            )}
          </div>

          {/* Submit Button */}
          <Button
            onClick={step === 'identifier' ? lookupPlayer : submitWeight}
            disabled={loading || (step === 'identifier' ? !identifierValue : !weight)}
            className="w-full h-20 lg:h-24 bg-[#FF3B30] hover:bg-[#D32F2F] text-white font-display text-2xl lg:text-3xl
              disabled:opacity-50 disabled:cursor-not-allowed active:scale-[0.98] transition-all"
            data-testid="submit-btn"
          >
            {loading ? (
              'PROCESSING...'
            ) : step === 'identifier' ? (
              <>
                <Check className="w-8 h-8 mr-3" />
                FIND ME
              </>
            ) : (
              <>
                <Scale className="w-8 h-8 mr-3" />
                LOG WEIGHT
              </>
            )}
          </Button>

          {step === 'weight' && (
            <Button
              onClick={handleDelete}
              variant="outline"
              className="w-full h-16 mt-4 border-[#27272A] text-[#A1A1AA] hover:bg-[#1A1A1A] text-xl"
              data-testid="delete-btn"
            >
              <Delete className="w-6 h-6 mr-2" />
              Delete
            </Button>
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-[#121212] border-t border-[#27272A] p-4 text-center">
        <p className="text-[#71717A] text-sm">
          Need help? Contact your coach or trainer.
        </p>
      </footer>
    </div>
  );
}
